import { z } from 'zod'
import { formaterJour } from './dates'
import { analyserMontant, formaterMontant } from './montant'
import { analyserPeriode, formaterPeriode } from './periode'

// Guides de déclaration : le pas-à-pas d'une démarche récurrente (acompte d'IS, liasse,
// taxe foncière…), écrit pour quelqu'un qui découvre l'administratif. Chaque étape dit où
// aller (site, menu), quoi regarder (à quoi ressemble le chiffre), pourquoi, quel chiffre
// relever, et ce qui reste un geste humain (payer, déclarer, valider, signer : N2, l'appli
// n'en fait aucun). Un guide est propre à une entité et à un type de règle : le même acompte
// ne se fait pas pareil chez Dougs et chez Numbr sur Tiime. Il se corrige au fil des
// passages : ce qui n'était pas au bon endroit se note en remarque sur l'étape, puis le guide
// est réécrit (journalisé, version incrémentée). La progression se garde sur la tâche.

const texte = z.string().trim().min(1)
const cle = z
  .string()
  .trim()
  .regex(/^[a-z0-9_-]{1,40}$/, 'clé : minuscules, chiffres, tiret ou souligné (40 au plus)')

export const NATURES_RELEVE = ['montant', 'date', 'oui_non', 'reference', 'texte'] as const
export type NatureReleve = (typeof NATURES_RELEVE)[number]

/** Au plus : un guide se parcourt sur un téléphone. */
export const ETAPES_GUIDE_MAX = 20
export const LEXIQUE_MAX = 20

export const etapeGuide = z.object({
  /** Identifiant stable (« compte-fiscal », « payer ») : la progression s'y accroche. */
  id: cle,
  titre: texte.max(200),
  /**
   * Tâches générées concernées, par leur action (verifier, payer, valider_liasse…). Absent :
   * l'étape vaut pour toutes ; une tâche libre (sans action) voit tout le guide.
   */
  actions: z.array(cle).max(10).optional(),
  /** Où aller : le site ou le logiciel, son adresse, et le chemin dans ses menus. */
  ou: z
    .object({
      site: texte.max(120),
      adresse: z.url().max(500).optional(),
      chemin: texte.max(400).optional(),
    })
    .optional(),
  /** Ce qu'on regarde et à quoi ça ressemble, en mots simples. */
  quoi: texte.max(2000),
  pourquoi: texte.max(600).optional(),
  /** Le chiffre ou la réponse à noter, gardé avec la tâche. */
  releve: z
    .object({
      cle,
      libelle: texte.max(120),
      nature: z.enum(NATURES_RELEVE),
      aide: texte.max(300).optional(),
    })
    .optional(),
  /** L'étape ne vaut que si un relevé précédent a cette valeur (« oui », « non »…). */
  si: z.object({ releve: cle, vaut: texte.max(40) }).optional(),
  /** Geste humain : payer, déclarer, valider, signer. Personne ne le fait à sa place. */
  humain: z.boolean().optional(),
  /** Ce qu'il faut garder ou joindre comme preuve. */
  preuve: texte.max(300).optional(),
  /** Si l'écran ne ressemble pas : quoi faire, qui appeler. */
  siBloque: texte.max(600).optional(),
})
export type EtapeGuide = z.infer<typeof etapeGuide>

export const termeLexique = z.object({ terme: texte.max(60), sens: texte.max(400) })

export const contenuGuide = z.object({
  titre: texte.max(200),
  /** Ce qu'est la démarche, en deux ou trois phrases, sans jargon. */
  objectif: texte.max(1500),
  lexique: z.array(termeLexique).max(LEXIQUE_MAX).default([]),
  etapes: z.array(etapeGuide).min(1).max(ETAPES_GUIDE_MAX),
})
export type ContenuGuide = z.infer<typeof contenuGuide>

/** Écrire un guide : son entité et son type (celui de la règle d'échéance, ou un code libre). */
export const guideEcriture = contenuGuide.extend({ entiteId: z.uuid(), type: cle })
export type GuideEcriture = z.infer<typeof guideEcriture>

/** Corriger une étape : la remplacer (même id), l'ajouter (après une autre), ou la retirer. */
export const correctionEtape = z.union([
  z.object({ etape: etapeGuide, apres: cle.nullable().optional() }),
  z.object({ retirer: cle }),
])
export type CorrectionEtape = z.infer<typeof correctionEtape>

/** Null si le guide se tient : identifiants distincts, conditions qui renvoient à un relevé antérieur. */
export function erreurGuide(g: Pick<ContenuGuide, 'etapes'>): string | null {
  const ids = new Set<string>()
  const releves = new Map<string, NatureReleve>()
  for (const e of g.etapes) {
    if (ids.has(e.id)) return `Deux étapes ont l'identifiant « ${e.id} ».`
    ids.add(e.id)
    if (e.si) {
      const nature = releves.get(e.si.releve)
      if (!nature)
        return `Étape « ${e.id} » : la condition renvoie au relevé « ${e.si.releve} », absent des étapes précédentes.`
      if (nature === 'oui_non' && !['oui', 'non'].includes(e.si.vaut))
        return `Étape « ${e.id} » : un relevé oui ou non se compare à « oui » ou « non ».`
    }
    if (e.releve) {
      if (releves.has(e.releve.cle)) return `Deux étapes relèvent « ${e.releve.cle} ».`
      releves.set(e.releve.cle, e.releve.nature)
    }
  }
  return null
}

/** Applique une correction d'étape au guide ; lève une erreur lisible si elle ne s'applique pas. */
export function corrigerEtapes(etapes: readonly EtapeGuide[], c: CorrectionEtape): EtapeGuide[] {
  if ('retirer' in c) {
    if (!etapes.some((e) => e.id === c.retirer)) throw new Error(`Étape « ${c.retirer} » absente.`)
    if (etapes.length === 1) throw new Error('Un guide garde au moins une étape.')
    return etapes.filter((e) => e.id !== c.retirer)
  }
  const i = etapes.findIndex((e) => e.id === c.etape.id)
  if (i >= 0 && c.apres === undefined) return etapes.map((e, j) => (j === i ? c.etape : e))
  const reste = etapes.filter((e) => e.id !== c.etape.id)
  if (c.apres === null || c.apres === undefined) {
    // Nouvelle étape sans position : à la fin ; « apres: null » : en tête.
    return c.apres === null ? [c.etape, ...reste] : [...reste, c.etape]
  }
  const k = reste.findIndex((e) => e.id === c.apres)
  if (k < 0) throw new Error(`Étape « ${c.apres} » absente : impossible de placer après.`)
  return [...reste.slice(0, k + 1), c.etape, ...reste.slice(k + 1)]
}

// Progression, gardée sur la tâche.

export const etatEtape = z.object({
  fait: z.boolean().optional(),
  faitLe: z.iso.datetime({ offset: true }).optional(),
  par: z.uuid().optional(),
  /** Valeur normalisée : centimes pour un montant, AAAA-MM-JJ pour une date, oui ou non. */
  valeur: z.string().max(200).optional(),
  /** Ce qui ne correspondait pas au guide : à reprendre dans le guide. */
  remarque: z.string().max(1000).optional(),
  remarqueLe: z.iso.datetime({ offset: true }).optional(),
})
export type EtatEtape = z.infer<typeof etatEtape>

export const guideTache = z.object({
  /** Type du guide choisi pour une tâche libre ; sinon celui de la règle de son échéance. */
  type: cle.optional(),
  /** Période pour une tâche libre (« 2025 », « 2026-T3 ») : situe l'exercice dans le guide. */
  periode: z.string().max(10).optional(),
  etapes: z.record(z.string(), etatEtape).default({}),
})
export type GuideTache = z.infer<typeof guideTache>

/** Associer un guide à une tâche (null le retire), avec la période d'une tâche libre. */
export const choixGuide = z.object({
  type: cle.nullable(),
  periode: z.string().trim().max(10).nullable().optional(),
})
export type ChoixGuide = z.infer<typeof choixGuide>

/** Avancer une étape : cocher, noter le chiffre relevé, laisser une remarque. */
export const avanceeEtape = z
  .object({
    etape: cle,
    fait: z.boolean().optional(),
    valeur: z.string().trim().max(200).nullable().optional(),
    remarque: z.string().trim().max(1000).nullable().optional(),
  })
  .refine(
    (a) => a.fait !== undefined || a.valeur !== undefined || a.remarque !== undefined,
    'Rien à noter : fait, valeur ou remarque.',
  )
export type AvanceeEtape = z.infer<typeof avanceeEtape>

/** Normalise une valeur saisie selon la nature du relevé ; lève une erreur lisible sinon. */
export function normaliserValeur(nature: NatureReleve, saisie: string): string {
  const v = saisie.trim()
  if (nature === 'montant') {
    const c = analyserMontant(v)
    if (c === null) throw new Error('Montant attendu, en euros (« 1 234,56 »).')
    return String(c)
  }
  if (nature === 'date') {
    const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v)
    const fr = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(v)
    const [a, m, j] = iso
      ? [iso[1]!, iso[2]!, iso[3]!]
      : fr
        ? [fr[3]!, fr[2]!.padStart(2, '0'), fr[1]!.padStart(2, '0')]
        : []
    const d = a ? new Date(`${a}-${m}-${j}T00:00:00Z`) : null
    if (!d || Number.isNaN(d.getTime()) || d.toISOString().slice(0, 10) !== `${a}-${m}-${j}`)
      throw new Error('Date attendue (« 15/09/2026 »).')
    return `${a}-${m}-${j}`
  }
  if (nature === 'oui_non') {
    const s = v.toLowerCase()
    if (['oui', 'o', 'yes', 'vrai'].includes(s)) return 'oui'
    if (['non', 'n', 'no', 'faux'].includes(s)) return 'non'
    throw new Error('Réponse attendue : oui ou non.')
  }
  if (!v) throw new Error('Valeur vide.')
  return v
}

/** La valeur gardée, telle qu'on la lit (« 1 234,56 € », « 15/09/2026 »). */
export function afficherValeur(nature: NatureReleve, valeur: string): string {
  if (nature === 'montant' && /^-?\d+$/.test(valeur)) return formaterMontant(Number(valeur))
  if (nature === 'date' && /^\d{4}-\d{2}-\d{2}$/.test(valeur)) return formaterJour(valeur)
  return valeur
}

/**
 * Applique une avancée à la progression d'une tâche. Une valeur vide (null) ou une remarque
 * vide efface ; décocher efface la date. Lève une erreur lisible (étape absente, valeur mal
 * formée, relevé sur une étape qui n'en a pas).
 */
export function avancerEtape(
  guide: Pick<ContenuGuide, 'etapes'>,
  progression: GuideTache,
  a: AvanceeEtape,
  qui: { par: string; le: string },
): GuideTache {
  const etape = guide.etapes.find((e) => e.id === a.etape)
  if (!etape) throw new Error(`Étape « ${a.etape} » absente du guide.`)
  const avant = progression.etapes[a.etape] ?? {}
  const apres: EtatEtape = { ...avant }
  if (a.valeur !== undefined) {
    if (!etape.releve) throw new Error(`L'étape « ${a.etape} » ne demande pas de chiffre.`)
    if (a.valeur === null || a.valeur === '') delete apres.valeur
    else apres.valeur = normaliserValeur(etape.releve.nature, a.valeur)
  }
  if (a.remarque !== undefined) {
    if (a.remarque === null || a.remarque === '') {
      delete apres.remarque
      delete apres.remarqueLe
    } else {
      apres.remarque = a.remarque
      apres.remarqueLe = qui.le
    }
  }
  if (a.fait === true) Object.assign(apres, { fait: true, faitLe: qui.le, par: qui.par })
  if (a.fait === false) {
    delete apres.fait
    delete apres.faitLe
    delete apres.par
  }
  const etapes = { ...progression.etapes }
  if (Object.keys(apres).length === 0) delete etapes[a.etape]
  else etapes[a.etape] = apres
  return { ...progression, etapes }
}

export type StatutEtapeGuide = 'faite' | 'a_faire' | 'en_attente' | 'sautee'

/** Les étapes qui concernent une tâche : celles de son action, ou toutes pour une tâche libre. */
export function etapesPourAction(
  etapes: readonly EtapeGuide[],
  action: string | null,
): EtapeGuide[] {
  if (!action) return [...etapes]
  const propres = etapes.filter((e) => !e.actions || e.actions.includes(action))
  // Une action que le guide ne connaît pas : tout le guide plutôt que rien.
  return propres.some((e) => e.actions?.includes(action)) ? propres : [...etapes]
}

/** Statut de chaque étape : faite, à faire, en attente d'une réponse antérieure, sautée. */
export function statutsEtapes(
  etapes: readonly EtapeGuide[],
  progression: GuideTache,
): Map<string, StatutEtapeGuide> {
  const valeurs = new Map<string, string>()
  for (const e of etapes) {
    const v = e.releve ? progression.etapes[e.id]?.valeur : undefined
    if (e.releve && v !== undefined) valeurs.set(e.releve.cle, v)
  }
  const statuts = new Map<string, StatutEtapeGuide>()
  for (const e of etapes) {
    if (progression.etapes[e.id]?.fait) statuts.set(e.id, 'faite')
    else if (!e.si) statuts.set(e.id, 'a_faire')
    else {
      const v = valeurs.get(e.si.releve)
      statuts.set(e.id, v === undefined ? 'en_attente' : v === e.si.vaut ? 'a_faire' : 'sautee')
    }
  }
  return statuts
}

// Rendu : les repères de l'entité posés dans le texte du guide.

/** Ce que le guide peut citer : `{entite}`, `{siren}`, `{cabinet}`, `{exercice}`… */
export type ReperesGuide = {
  entite: string
  siren: string | null
  cabinet: string | null
  /** Comptes de l'entité, « Banque (libellé) ». */
  comptes: string[]
  periode: string | null
  /** Date limite AAAA-MM-JJ (échéance, sinon date due de la tâche). */
  dateLimite: string | null
  montantPrevuCts: number | null
}

export const MANQUANT = '[à compléter]'

/** Valeur de chaque repère, en texte ; absent : « [à compléter] ». */
export function valeursReperes(r: ReperesGuide): Record<string, string> {
  const p = r.periode ? analyserPeriode(r.periode) : null
  const exercice = p ? p.annee : r.dateLimite ? Number(r.dateLimite.slice(0, 4)) : null
  return {
    entite: r.entite,
    siren: r.siren ?? MANQUANT,
    cabinet: r.cabinet ?? MANQUANT,
    compte: r.comptes.length > 0 ? r.comptes.join(' ou ') : MANQUANT,
    periode: r.periode ? formaterPeriode(r.periode) : MANQUANT,
    exercice: exercice ? String(exercice) : MANQUANT,
    exercice_precedent: exercice ? String(exercice - 1) : MANQUANT,
    annee: r.dateLimite ? r.dateLimite.slice(0, 4) : exercice ? String(exercice) : MANQUANT,
    date_limite: r.dateLimite ? formaterJour(r.dateLimite) : MANQUANT,
    montant_prevu: r.montantPrevuCts !== null ? formaterMontant(r.montantPrevuCts) : MANQUANT,
  }
}

/** Remplace `{repere}` ; un repère inconnu reste tel quel, pour être vu et corrigé. */
export function poserReperes(t: string, valeurs: Record<string, string>): string {
  return t.replace(/\{([a-z_]+)\}/g, (tout, nom: string) => valeurs[nom] ?? tout)
}

function poserDansEtape(e: EtapeGuide, v: Record<string, string>): EtapeGuide {
  const p = (t: string | undefined) => (t === undefined ? undefined : poserReperes(t, v))
  return {
    ...e,
    titre: poserReperes(e.titre, v),
    quoi: poserReperes(e.quoi, v),
    ...(e.ou && {
      ou: {
        site: poserReperes(e.ou.site, v),
        ...(e.ou.adresse && { adresse: e.ou.adresse }),
        ...(e.ou.chemin && { chemin: poserReperes(e.ou.chemin, v) }),
      },
    }),
    ...(e.pourquoi && { pourquoi: p(e.pourquoi) }),
    ...(e.preuve && { preuve: p(e.preuve) }),
    ...(e.siBloque && { siBloque: p(e.siBloque) }),
    ...(e.releve && {
      releve: { ...e.releve, libelle: poserReperes(e.releve.libelle, v) },
    }),
  }
}

export type EtapeRendue = EtapeGuide & {
  statut: StatutEtapeGuide
  etat: EtatEtape | null
  /** Valeur relevée, lisible. */
  valeurLue: string | null
}

/**
 * Le guide d'une tâche, prêt à lire : repères posés, étapes de son action avec leur statut,
 * et la prochaine étape à faire (ou en attente d'une réponse).
 */
export function rendreGuide(
  guide: ContenuGuide,
  reperes: ReperesGuide,
  progression: GuideTache,
  action: string | null,
) {
  const v = valeursReperes(reperes)
  const etapes = etapesPourAction(guide.etapes, action)
  const statuts = statutsEtapes(etapes, progression)
  const rendues: EtapeRendue[] = etapes.map((e) => {
    const etat = progression.etapes[e.id] ?? null
    return {
      ...poserDansEtape(e, v),
      statut: statuts.get(e.id)!,
      etat,
      valeurLue: e.releve && etat?.valeur ? afficherValeur(e.releve.nature, etat.valeur) : null,
    }
  })
  const utiles = rendues.filter((e) => e.statut !== 'sautee')
  const prochaine = rendues.find((e) => e.statut === 'a_faire' || e.statut === 'en_attente')
  return {
    titre: poserReperes(guide.titre, v),
    objectif: poserReperes(guide.objectif, v),
    lexique: guide.lexique,
    reperes: v,
    etapes: rendues,
    faites: utiles.filter((e) => e.statut === 'faite').length,
    total: utiles.length,
    prochaine: prochaine?.id ?? null,
    termine: utiles.length > 0 && utiles.every((e) => e.statut === 'faite'),
  }
}
export type GuideRendu = ReturnType<typeof rendreGuide>

/**
 * Méthode d'accompagnement, rendue avec le guide à l'assistant (MCP, chat de l'appli) : une
 * personne qui découvre l'administratif, une étape à la fois.
 */
export const ACCOMPAGNER_DECLARATION = `Tu accompagnes une personne qui découvre l'administratif, pas à pas, avec le guide de la tâche. Méthode :
1. Situer en deux phrases : ce qu'est la démarche (objectif), l'entité, l'exercice ou la période, la date limite. Expliquer chaque mot technique la première fois (lexique), sans jargon.
2. Une seule étape à la fois, en commençant par la prochaine. Dire où aller (site, adresse, chemin des menus), quoi regarder et à quoi ça ressemble, pourquoi on le fait. Demander ce qu'elle voit, attendre sa réponse.
3. Quand elle donne un chiffre ou une réponse : le noter (outil guide, etape et valeur), le relire avec elle, puis cocher l'étape (fait). Une étape « si » ne vaut que selon une réponse précédente : la sauter sinon.
4. Si l'écran ne ressemble pas au guide : chercher avec elle (siBloque), puis noter une remarque sur l'étape (ce qui était réellement affiché, où) et proposer de corriger le guide (outil guide, corriger) quand vous avez trouvé : le prochain passage sera juste.
5. Les étapes « humain » (payer, déclarer, valider, signer) : elle les fait elle-même. Tu expliques et tu vérifies avec elle avant qu'elle clique, jamais à sa place.
6. Croiser avec l'appli quand c'est utile : paiements et relevés (prélèvement DGFiP déjà passé ?), pièces (avis, liasse), fils de mails du cabinet.
7. À la fin : récapituler les chiffres relevés, dire ce qui reste et quand, proposer de terminer la tâche (modifier_taches) et de joindre la preuve (ajouter_piece).
Ne jamais inventer un chiffre ni un menu : un repère « [à compléter] » ou une étape marquée « à confirmer » se vérifie avec elle à l'écran.`
